import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { generateBudgetPdfFilename } from './pdfFilename';

// Color constants (matching Tailwind theme)
const PRIMARY: [number, number, number] = [79, 70, 229]; // indigo-600
const HEADER_BG: [number, number, number] = [249, 250, 251]; // gray-50
const TEXT_PRIMARY: [number, number, number] = [17, 24, 39]; // gray-900
const TEXT_SECONDARY: [number, number, number] = [107, 114, 128]; // gray-500
const GREEN: [number, number, number] = [22, 163, 74]; // green-600
const RED: [number, number, number] = [220, 38, 38]; // red-600

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export interface BudgetPdfInventoryItem {
  totalPrice?: number | null;
  unitPrice?: number | null;
  totalCost?: number | null;
  unitCost?: number | null;
  isOwnItem?: boolean;
  isSubcontracted?: boolean;
  originalOrganizationName?: string;
  chargeToClient?: boolean;
  amount?: number;
  name?: string;
  equipmentId?: string;
  time?: string;
  taskName?: string;
  unassigned?: boolean;
  displayOrganizationName?: string;
  costPerHour?: number | null;
  source?: 'WAREHOUSE' | 'MANUAL';
}

export interface BudgetPdfProjectInfo {
  name?: string;
  budget?: number | null;
  originalProjectId?: string;
  originalProject?: {
    projectId: string;
    name: string;
    organization: { name?: string };
  };
  client?: { name?: string };
  projectManager?: {
    user?: { firstName?: string; lastName?: string };
  };
  superintendents?: Array<{
    user?: { firstName?: string; lastName?: string };
  }>;
  state?: string;
}

export interface BudgetPdfOptions {
  inventory: BudgetPdfInventoryItem[];
  project: BudgetPdfProjectInfo;
  includeCosts: boolean;
  isDelegated: boolean;
  filterLabel: string;
}

export function generateBudgetPdf({
  inventory,
  project,
  includeCosts,
  isDelegated,
  filterLabel,
}: BudgetPdfOptions): void {
  const doc = new jsPDF('landscape', 'mm', 'letter');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  const projectName = project.name || 'Project';
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const totalCost = inventory.reduce(
    (sum, item) => sum + (item.totalCost || 0),
    0,
  );
  const materialCount = inventory.filter((item) => !item.equipmentId).length;
  const equipmentCount = inventory.filter((item) => !!item.equipmentId).length;
  const hasBudget =
    !project.originalProjectId &&
    !!project.budget &&
    project.budget > 0;
  const isOverBudget = totalCost > (project.budget || 0);
  const percentageSpent = hasBudget
    ? ((totalCost / (project.budget || 1)) * 100).toFixed(2)
    : null;

  // ========================
  // HEADER BAR
  // ========================
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, 20, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`${projectName} — Budget Details`, margin, 13);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(today, pageWidth - margin, 13, { align: 'right' });

  // ========================
  // PROJECT INFO ROW
  // ========================
  let infoY = 26;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');

  const infoItems: string[] = [];

  if (isDelegated && project.originalProject?.organization?.name) {
    infoItems.push(`From: ${project.originalProject.organization.name}`);
  }
  if (project.client?.name) {
    infoItems.push(`Client: ${project.client.name}`);
  }
  if (project.projectManager?.user) {
    const pm = project.projectManager.user;
    infoItems.push(`PM: ${pm.firstName} ${pm.lastName}`);
  }
  if (project.superintendents?.[0]?.user) {
    const supt = project.superintendents[0].user;
    infoItems.push(`Supt: ${supt.firstName} ${supt.lastName}`);
  }
  if (project.state) {
    infoItems.push(`Status: ${project.state}`);
  }
  if (filterLabel !== 'All Items') {
    infoItems.push(`Filter: ${filterLabel}`);
  }

  if (infoItems.length > 0) {
    doc.setTextColor(...TEXT_SECONDARY);
    doc.text(infoItems.join('   |   '), margin, infoY);
    infoY += 6;
  }

  // ========================
  // SUMMARY STATS
  // ========================
  const statsY = infoY + 2;
  const statsCount = includeCosts ? (hasBudget ? 5 : 3) : 2;
  const cardGap = 4;
  const cardWidth =
    (pageWidth - margin * 2 - cardGap * (statsCount - 1)) / statsCount;
  const cardHeight = 18;

  const stats: { label: string; value: string; color: [number, number, number] }[] = [
    { label: 'Materials', value: String(materialCount), color: PRIMARY },
    { label: 'Equipment', value: String(equipmentCount), color: PRIMARY },
  ];

  if (includeCosts) {
    stats.push({
      label: 'Total Cost',
      value: formatCurrency(totalCost),
      color: isOverBudget ? RED : GREEN,
    });
    if (hasBudget) {
      stats.push({
        label: 'Max Budget',
        value: formatCurrency(project.budget),
        color: PRIMARY,
      });
      stats.push({
        label: 'Spent',
        value: `${percentageSpent}%`,
        color: isOverBudget ? RED : GREEN,
      });
    }
  }

  stats.forEach((stat, i) => {
    const x = margin + i * (cardWidth + cardGap);

    doc.setFillColor(245, 245, 250);
    doc.roundedRect(x, statsY, cardWidth, cardHeight, 2, 2, 'F');

    doc.setTextColor(...TEXT_SECONDARY);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.label, x + 4, statsY + 7);

    doc.setTextColor(...stat.color);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(stat.value, x + 4, statsY + 14);
  });

  // ========================
  // TABLE
  // ========================
  const tableColumns = includeCosts
    ? [
        { header: 'Item', dataKey: 'name' },
        { header: 'Type', dataKey: 'type' },
        { header: 'Amount', dataKey: 'amount' },
        { header: 'Unit Cost', dataKey: 'unitCost' },
        { header: 'Cost/Hour', dataKey: 'costPerHour' },
        { header: 'Total Cost', dataKey: 'totalCost' },
        { header: 'Date', dataKey: 'date' },
        { header: 'Task', dataKey: 'task' },
        { header: 'Subcontracted', dataKey: 'subcontracted' },
        { header: 'Organization', dataKey: 'organization' },
        { header: 'Source', dataKey: 'source' },
      ]
    : [
        { header: 'Item', dataKey: 'name' },
        { header: 'Type', dataKey: 'type' },
        { header: 'Amount', dataKey: 'amount' },
        { header: 'Date', dataKey: 'date' },
        { header: 'Task', dataKey: 'task' },
        { header: 'Subcontracted', dataKey: 'subcontracted' },
        { header: 'Organization', dataKey: 'organization' },
        { header: 'Source', dataKey: 'source' },
      ];

  const getSubcontractedLabel = (item: BudgetPdfInventoryItem): string => {
    if (item.unassigned && item.isOwnItem) return 'Previously Contracted by';
    if (item.unassigned) return 'Previous contractor';
    return item.isOwnItem ? 'No' : 'Yes';
  };

  const getOrganizationLabel = (item: BudgetPdfInventoryItem): string => {
    if (!item.isOwnItem && item.originalOrganizationName !== 'Unknown') {
      return item.originalOrganizationName || '-';
    }
    if (item.unassigned && item.isOwnItem && item.displayOrganizationName) {
      return item.displayOrganizationName;
    }
    return '-';
  };

  const tableRows = inventory.map((item) => ({
    name: item.name || '-',
    type: item.equipmentId ? 'Equipment' : 'Material',
    amount: item.amount != null ? String(item.amount) : '-',
    unitCost: formatCurrency(item.unitCost),
    costPerHour: formatCurrency(item.costPerHour),
    totalCost: formatCurrency(item.totalCost),
    date: formatDate(item.time),
    task: item.taskName || '-',
    subcontracted: getSubcontractedLabel(item),
    organization: getOrganizationLabel(item),
    source: item.source || '-',
  }));

  autoTable(doc, {
    startY: statsY + cardHeight + 8,
    columns: tableColumns,
    body: tableRows,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 7,
      cellPadding: 2.5,
      textColor: TEXT_PRIMARY,
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: HEADER_BG,
      textColor: TEXT_SECONDARY,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'left',
    },
    columnStyles: {
      amount: { halign: 'right' },
      unitCost: { halign: 'right' },
      costPerHour: { halign: 'right' },
      totalCost: { halign: 'right' },
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255],
    },
    didDrawPage: () => {
      const pageNumber = doc.getCurrentPageInfo().pageNumber;

      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

      doc.setFontSize(7);
      doc.setTextColor(...TEXT_SECONDARY);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on ${today}`, margin, pageHeight - 7);

      const modeLabel = includeCosts ? 'With Costs' : 'Materials Only';
      doc.text(
        `${modeLabel}  —  Page ${pageNumber} of {total_pages}`,
        pageWidth - margin,
        pageHeight - 7,
        { align: 'right' },
      );
    },
  });

  // Replace total pages placeholder
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.putTotalPages('{total_pages}');
  }

  // Save
  const filename = generateBudgetPdfFilename(projectName, includeCosts);
  doc.save(filename);
}

// ========================
// EXPORT ALL — Unified budget summary PDF
// ========================

export interface AllBudgetPdfRow {
  category: string;
  name: string;
  totalCost: number;
}

export interface AllBudgetPdfOptions {
  rows: AllBudgetPdfRow[];
  project: BudgetPdfProjectInfo;
  totals: {
    materials: number;
    equipment: number;
    delegated: number;
    lineItems: number;
    total: number;
  };
}

export function generateAllBudgetPdf({
  rows,
  project,
  totals,
}: AllBudgetPdfOptions): void {
  const doc = new jsPDF('landscape', 'mm', 'letter');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  const projectName = project.name || 'Project';
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const hasBudget =
    !project.originalProjectId &&
    !!project.budget &&
    project.budget > 0;
  const isOverBudget = totals.total > (project.budget || 0);
  const percentageSpent = hasBudget
    ? ((totals.total / (project.budget || 1)) * 100).toFixed(2)
    : null;

  // ========================
  // HEADER BAR
  // ========================
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, 20, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`${projectName} — Full Budget Summary`, margin, 13);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(today, pageWidth - margin, 13, { align: 'right' });

  // ========================
  // SUMMARY STATS
  // ========================
  const statsY = 26;
  const statsCount = hasBudget ? 6 : 4;
  const cardGap = 4;
  const cardWidth =
    (pageWidth - margin * 2 - cardGap * (statsCount - 1)) / statsCount;
  const cardHeight = 18;

  const stats: { label: string; value: string; color: [number, number, number] }[] = [
    { label: 'Materials', value: formatCurrency(totals.materials), color: PRIMARY },
    { label: 'Equipment', value: formatCurrency(totals.equipment), color: PRIMARY },
    { label: 'Delegated', value: formatCurrency(totals.delegated), color: PRIMARY },
    { label: 'Line Items', value: formatCurrency(totals.lineItems), color: PRIMARY },
  ];

  if (hasBudget) {
    stats.push({
      label: 'Max Budget',
      value: formatCurrency(project.budget),
      color: PRIMARY,
    });
    stats.push({
      label: 'Spent',
      value: `${percentageSpent}%`,
      color: isOverBudget ? RED : GREEN,
    });
  }

  stats.forEach((stat, i) => {
    const x = margin + i * (cardWidth + cardGap);
    doc.setFillColor(245, 245, 250);
    doc.roundedRect(x, statsY, cardWidth, cardHeight, 2, 2, 'F');

    doc.setTextColor(...TEXT_SECONDARY);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.label, x + 4, statsY + 7);

    doc.setTextColor(...stat.color);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(stat.value, x + 4, statsY + 14);
  });

  // ========================
  // TABLE
  // ========================
  const tableColumns = [
    { header: 'Category', dataKey: 'category' },
    { header: 'Name / Description', dataKey: 'name' },
    { header: 'Total Cost', dataKey: 'totalCost' },
  ];

  const tableRows = rows.map((row) => ({
    category: row.category,
    name: row.name,
    totalCost: formatCurrency(row.totalCost),
  }));

  // Grand total row
  tableRows.push({
    category: '',
    name: 'GRAND TOTAL',
    totalCost: formatCurrency(totals.total),
  });

  autoTable(doc, {
    startY: statsY + cardHeight + 8,
    columns: tableColumns,
    body: tableRows,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: TEXT_PRIMARY,
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: HEADER_BG,
      textColor: TEXT_SECONDARY,
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'left',
    },
    columnStyles: {
      totalCost: { halign: 'right' },
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255],
    },
    didParseCell: (data) => {
      // Bold the grand total row
      if (data.row.index === tableRows.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [245, 245, 250];
      }
    },
    didDrawPage: () => {
      const pageNumber = doc.getCurrentPageInfo().pageNumber;

      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

      doc.setFontSize(7);
      doc.setTextColor(...TEXT_SECONDARY);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on ${today}`, margin, pageHeight - 7);
      doc.text(
        `Full Budget Summary  —  Page ${pageNumber} of {total_pages}`,
        pageWidth - margin,
        pageHeight - 7,
        { align: 'right' },
      );
    },
  });

  // Replace total pages placeholder
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.putTotalPages('{total_pages}');
  }

  // Save
  const sanitizedName = projectName.replace(/[<>:"/\\|?*]/g, '').replace(/\s+/g, '_').substring(0, 50) || 'Project';
  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`${sanitizedName}_Budget_All_${dateStr}.pdf`);
}

// ========================
// EXPORT DELEGATED TASKS PDF
// ========================

export interface DelegatedTaskPdfItem {
  taskName: string;
  organizationName: string;
  budget: number;
}

export interface DelegatedTasksPdfOptions {
  items: DelegatedTaskPdfItem[];
  projectName: string;
}

export function generateDelegatedTasksPdf({
  items,
  projectName,
}: DelegatedTasksPdfOptions): void {
  const doc = new jsPDF('landscape', 'mm', 'letter');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const total = items.reduce((sum, d) => sum + (d.budget || 0), 0);

  // Header bar
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, 20, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`${projectName} — Delegated Task Costs`, margin, 13);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(today, pageWidth - margin, 13, { align: 'right' });

  // Table
  const tableRows = items.map((d) => ({
    taskName: d.taskName,
    organizationName: d.organizationName,
    budget: formatCurrency(d.budget),
  }));

  tableRows.push({
    taskName: '',
    organizationName: 'TOTAL DELEGATED COST',
    budget: formatCurrency(total),
  });

  autoTable(doc, {
    startY: 28,
    columns: [
      { header: 'Task Name', dataKey: 'taskName' },
      { header: 'Delegated To', dataKey: 'organizationName' },
      { header: 'Accepted Budget', dataKey: 'budget' },
    ],
    body: tableRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 8, cellPadding: 3, textColor: TEXT_PRIMARY, lineColor: [229, 231, 235], lineWidth: 0.1 },
    headStyles: { fillColor: HEADER_BG, textColor: TEXT_SECONDARY, fontStyle: 'bold', fontSize: 8 },
    columnStyles: { budget: { halign: 'right' } },
    didParseCell: (data) => {
      if (data.row.index === tableRows.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [245, 245, 250];
      }
    },
  });

  const sanitizedName = projectName.replace(/[<>:"/\\|?*]/g, '').replace(/\s+/g, '_').substring(0, 50) || 'Project';
  doc.save(`${sanitizedName}_Delegated_Tasks_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ========================
// EXPORT OTHER EXPENSES PDF
// ========================

export interface ExpensePdfItem {
  description: string;
  cost: number;
  chargeToClient: boolean;
  isOwnItem: boolean;
  organizationName: string | null;
}

export interface ExpensesPdfOptions {
  items: ExpensePdfItem[];
  projectName: string;
}

export function generateExpensesPdf({
  items,
  projectName,
}: ExpensesPdfOptions): void {
  const doc = new jsPDF('landscape', 'mm', 'letter');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const ownItems = items.filter((e) => e.isOwnItem);
  const subItems = items.filter((e) => !e.isOwnItem);
  const totalOwn = ownItems.reduce((sum, e) => sum + (e.cost || 0), 0);
  const totalSub = subItems.reduce((sum, e) => sum + (e.cost || 0), 0);

  // Header bar
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, 20, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`${projectName} — Other Expenses`, margin, 13);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(today, pageWidth - margin, 13, { align: 'right' });

  let startY = 28;

  // Own expenses table
  if (ownItems.length > 0) {
    doc.setTextColor(...TEXT_PRIMARY);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Your Expenses', margin, startY);

    const ownRows = ownItems.map((e) => ({
      description: e.description,
      cost: formatCurrency(e.cost),
      chargeToClient: e.chargeToClient ? 'Yes' : 'No',
    }));
    ownRows.push({ description: 'TOTAL', cost: formatCurrency(totalOwn), chargeToClient: '' });

    autoTable(doc, {
      startY: startY + 4,
      columns: [
        { header: 'Description', dataKey: 'description' },
        { header: 'Cost', dataKey: 'cost' },
        { header: 'Charge to Client', dataKey: 'chargeToClient' },
      ],
      body: ownRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 3, textColor: TEXT_PRIMARY, lineColor: [229, 231, 235], lineWidth: 0.1 },
      headStyles: { fillColor: HEADER_BG, textColor: TEXT_SECONDARY, fontStyle: 'bold', fontSize: 8 },
      columnStyles: { cost: { halign: 'right' } },
      didParseCell: (data) => {
        if (data.row.index === ownRows.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [245, 245, 250];
        }
      },
    });

    startY = (doc as any).lastAutoTable.finalY + 10;
  }

  // Subcontractor expenses table
  if (subItems.length > 0) {
    doc.setTextColor(...TEXT_PRIMARY);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Subcontractor Charges', margin, startY);

    const subRows = subItems.map((e) => ({
      description: e.description,
      cost: formatCurrency(e.cost),
      organization: e.organizationName || '-',
    }));
    subRows.push({ description: 'TOTAL', cost: formatCurrency(totalSub), organization: '' });

    autoTable(doc, {
      startY: startY + 4,
      columns: [
        { header: 'Description', dataKey: 'description' },
        { header: 'Cost', dataKey: 'cost' },
        { header: 'Organization', dataKey: 'organization' },
      ],
      body: subRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 3, textColor: TEXT_PRIMARY, lineColor: [229, 231, 235], lineWidth: 0.1 },
      headStyles: { fillColor: HEADER_BG, textColor: TEXT_SECONDARY, fontStyle: 'bold', fontSize: 8 },
      columnStyles: { cost: { halign: 'right' } },
      didParseCell: (data) => {
        if (data.row.index === subRows.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [245, 245, 250];
        }
      },
    });
  }

  const sanitizedName = projectName.replace(/[<>:"/\\|?*]/g, '').replace(/\s+/g, '_').substring(0, 50) || 'Project';
  doc.save(`${sanitizedName}_Other_Expenses_${new Date().toISOString().slice(0, 10)}.pdf`);
}
